// The value → colour mapping, derived from the REGION instead of from the render.
//
// This is FINAL-PHASE-PLAN.md F2, and it is the file that makes D4's central claim
// true. The old renderer histogram-equalised *within each render*: it sorted the
// escaped subpixels of the image it was currently producing and used each one's
// rank as its position on the palette ramp. That gives a beautiful, evenly-used
// ramp at any zoom depth — and it makes a pixel's colour depend on how many pixels
// happen to be in the render, so the same region at two sizes is two slightly
// different pictures. Measured: 0.0124 mean ΔE at thumbnail scale, 7.6% of pixels
// visibly different from the preview. Same modules ≠ same image.
//
// So: derive the mapping ONCE from the region, then apply it as a plain lookup at
// any output resolution. Colour becomes a function of the bounds alone. Thumbnail,
// generator preview, og:image card and the 300dpi print land on the same colours,
// which is not merely a rescue of the uniqueness story but a strengthening of it —
// "this image is uniquely yours" now has no free parameters in it at all.
//
// ⚠️ This deliberately reopens a Block H note that recorded the thumbnail/full-
// render colour difference as "correct, not a bug — don't let anyone fix this".
// That note exists to stop someone hacking the THUMBNAIL PATH to paper over the
// difference, and it is still binding on any such local fix. This is the opposite
// move: it removes the difference at the source, for every consumer at once.

import { calcReal, calcImaginary } from './pointer.js';
import { escapeVelocity } from './escape.js';
import { PALETTE_LUT_SIZE } from './palette.js';
import coordId from './coordId.js';

// The reference sample grid: LATTICE × LATTICE escape evaluations spread over the
// region, which is what the mapping is derived from.
//
// F2 suggests deriving it from "a canonical reference render — a fixed size,
// naturally the one D6's determinism is already defined at", i.e. RENDER_CAP.
// That is 1,000,000 escape evaluations, and it cannot be what happens here: a
// gallery thumbnail is ~14,000 pixels, so a full-size reference pass would make
// every thumbnail seventy times more expensive than the thumbnail itself, and the
// landing page renders six of them before the visitor has done anything. The
// render-cost trap would eat the change whole.
//
// 128×128 = 16,384 samples is enough, and the reason is worth stating because it
// is not the obvious one. This is not an approximation of "the true CDF" that we
// would rather have computed exactly. What F2 is about is DRIFT BETWEEN RENDER
// SIZES, and the lattice's own sampling error is *identical for every render of
// the region*, because it is a deterministic function of the bounds. A coarse
// estimate that every consumer shares is exactly the property being bought; a
// perfect estimate would not buy any more of it. Sampling error at 16k samples is
// ~0.4% of a quantile either way, against a palette quantised into 2048 steps.
//
// Cost, for scale: 16,384 evaluations against a full-screen render's ~8.6 MILLION
// subpixels (0.2%), or a thumbnail's ~56,000 (29%, paid once per region and then
// cached below — so the landing page's soft hero and sharp hero share one).
export const LATTICE = 128;

// The table has one entry per palette LUT entry on purpose: `map[i]` is the escape
// value at which colour `i` starts, so a lookup lands directly on a palette index
// with no scaling arithmetic, and the mapping's resolution can never disagree with
// the ramp's.
export const MAP_STOPS = PALETTE_LUT_SIZE;

// Build the inverse CDF for a region: a non-decreasing table of escape-value
// thresholds, one per palette entry. Returns null for a region where nothing in
// the lattice escaped (a box wholly inside the set) — there is no distribution to
// describe, and callers handle it explicitly rather than being handed a fake one.
export const buildColourMap = ({ realMin, realMax, imaginaryMin, imaginaryMax }) => {

    // Interior points contribute no colour (they get INTERIOR_RGB), so they are
    // excluded from the distribution exactly as they were excluded from the sort.
    const values = [];
    for (let x = 0; x < LATTICE; x++) {
        const real = calcReal(x, realMin, realMax, LATTICE);
        for (let y = 0; y < LATTICE; y++) {
            const imaginary = calcImaginary(y, imaginaryMin, imaginaryMax, LATTICE);
            const value = escapeVelocity(real, imaginary);
            if (!Number.isNaN(value)) values.push(value);
        }
    }

    if (values.length === 0) return null;

    values.sort((a, b) => a - b);

    // Sample the sorted values at MAP_STOPS evenly spaced quantiles. This is the
    // inverse CDF: map[i] is the value at quantile i/(MAP_STOPS-1), so "what
    // fraction of the region escapes at or below v" is answered by finding v's
    // position in this table — which is precisely the rank the old code computed
    // by sorting the whole render.
    const map = new Float64Array(MAP_STOPS);
    const last = values.length - 1;
    for (let i = 0; i < MAP_STOPS; i++) {
        map[i] = values[Math.round((i / (MAP_STOPS - 1)) * last)];
    }
    return map;
};

// Escape value → palette index, by binary search over the thresholds.
//
// Called once per subpixel — millions of times per render — so it is written flat:
// no allocation, no closure, ~11 iterations over a 16KB table that stays in cache.
// It replaces a full sort of every escaped subpixel in the render, which is both
// the largest allocation the renderer made and its only superlinear step.
export const paletteIndexFor = (map, value) => {
    const top = MAP_STOPS - 1;
    if (value <= map[0]) return 0;
    if (value >= map[top]) return top;

    // Largest i with map[i] <= value.
    let lo = 0;
    let hi = top;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (map[mid] <= value) lo = mid; else hi = mid - 1;
    }

    // Nearest threshold rather than the one below, matching the rounding the old
    // rank calculation did (`... * maxLut + 0.5 | 0`). Half a palette entry out of
    // 2048 either way, but free to get right.
    return (lo < top && value - map[lo] > map[lo + 1] - value) ? lo + 1 : lo;
};

// ---- Cache -------------------------------------------------------------------
//
// Keyed by coordId, which is the canonical identity of a region, so any two
// callers asking about the same specimen get the same table — and, more to the
// point, the same table *object*, so a gallery entry rendered as a thumbnail and
// then full-screen pays for the lattice once. The landing page is the case this
// was written for: the soft hero and the sharp hero are the same region, and
// before this change they were also two different colourings of it.
//
// FIFO with a hard cap rather than an LRU: a few hundred KB of tables is not worth
// the bookkeeping, and the access pattern (a gallery being scrolled) is close
// enough to insertion order that recency would buy little.
const CACHE_LIMIT = 64;
const cache = new Map();

export const colourMapFor = (bounds) => {
    const key = coordId(bounds);
    if (cache.has(key)) return cache.get(key);

    const map = buildColourMap(bounds);
    if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
    cache.set(key, map);
    return map;
};

// Tests need to prove the cache is not what is making things look consistent.
export const clearColourMapCache = () => cache.clear();
